import React, { useState, useEffect, useRef } from "react";
import {
  createOrder,
  fetchCustomers,
  fetchCaptains,
  fetchDeliveries,
} from "../../../../../../../services/pos/Tables";
import {
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  InputLabel,
  
  FormControl,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ShowTableReservation from "./ShowTableReservation";

const CreateOrderForm = ({ tableId, rootId, onSuccess, hasReservation }) => {
  const [formData, setFormData] = useState({
    order_type: localStorage.getItem("current_component") || "tables",
    customer_id: "",
    customer_name: "",
    email: "",
    customer_phones: [""],
    customer_addresses: [""], // <-- use array for addresses
    captain_order_id: "",
    delivery_id: "",
  });

  const [customers, setCustomers] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showReservationDialog, setShowReservationDialog] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      table_id: tableId,
    }));
  }, [tableId]);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomers();
        setCustomers(data || []);
      } catch (err) {
        console.error("فشل تحميل العملاء", err);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadCaptains = async () => {
      try {
        const data = await fetchCaptains();
        setCaptains(data || []);
      } catch (err) {
        console.error("فشل تحميل الكباتن", err);
      }
    };
    loadCaptains();
  }, []);

  useEffect(() => {
    const loadDeliveries = async () => {
      try {
        const data = await fetchDeliveries();
        setDeliveries(data || []);
      } catch (err) {
        console.error("فشل تحميل الدليفري", err);
      }
    };
    loadDeliveries();
  }, []);

  // ✅ useEffect لإضافة اختصار Enter لتنفيذ الإرسال
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من مفتاح Enter
      if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault(); // منع السلوك الافتراضي
        if (formRef.current) {
          formRef.current.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          );
        }
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle phone change for dynamic fields
  const handlePhoneChange = (idx, value) => {
    const phones = [...formData.customer_phones];
    phones[idx] = value; // لا تضف + تلقائياً
    setFormData((prev) => ({ ...prev, customer_phones: phones }));
  };

  const handleAddPhone = () => {
    setFormData((prev) => ({
      ...prev,
      customer_phones: [...prev.customer_phones, ""],
    }));
  };

  const handleRemovePhone = (idx) => {
    setFormData((prev) => ({
      ...prev,
      customer_phones: prev.customer_phones.filter((_, i) => i !== idx),
    }));
  };

  // Handle address change for dynamic fields
  const handleAddressChange = (idx, value) => {
    const addresses = [...formData.customer_addresses];
    addresses[idx] = value;
    setFormData((prev) => ({ ...prev, customer_addresses: addresses }));
  };

  const handleAddAddress = () => {
    setFormData((prev) => ({
      ...prev,
      customer_addresses: [...prev.customer_addresses, ""],
    }));
  };

  const handleRemoveAddress = (idx) => {
    setFormData((prev) => ({
      ...prev,
      customer_addresses: prev.customer_addresses.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const phones = formData.customer_phones;
      const addresses = formData.customer_addresses;
      const payload = {
        ...formData,
        customer_name:
          formData.customer_name && formData.customer_name.trim() !== ""
            ? formData.customer_name
            : "TablesCustomer", // إذا لم يُكتب اسم عميل
        customer_phone1: phones[0] || "",
        customer_phone2: phones[1] || "",
        customer_phone3: phones[2] || "",
        customer_address1: addresses[0] || "",
        customer_address2: addresses[1] || "",
        customer_address3: addresses[2] || "",
      };
      delete payload.customer_phones;
      delete payload.customer_addresses;

      const res = await createOrder(payload);
      setResult({ success: true, data: res });

      if (onSuccess && res?.data?.sttus === "open") {
        onSuccess({
          id: res.data.order_id,
          status: res.data.sttus,
        });
      }
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: { xs: 1, sm: 2, md: 4 },
        maxWidth: { xs: "99vw", sm: 500, md: 700 },
        margin: "auto",
        mt: { xs: 1, sm: 2, md: 4 },
        borderRadius: 3,
        width: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={3}
        color="#fff"
        align="center"
        sx={{
          fontSize: { xs: "1.3rem", sm: "2rem" },
          wordBreak: "break-word",
        }}
      >
        T-{tableId}
      </Typography>
      {/* تنبيه إذا كان هناك حجز على الطاولة مع زر عرض الحجوزات */}
      {hasReservation && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Alert
            severity="warning"
            sx={{ fontWeight: "bold", fontSize: "1.1em", m: 0 }}
          >
            ⚠️ يوجد حجز على هذه الطاولة
          </Alert>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setShowReservationDialog(true)}
          >
            عرض الحجوزات
          </Button>
        </Box>
      )}
      {/* Dialog لعرض الحجوزات */}
      <Dialog
        open={showReservationDialog}
        onClose={() => setShowReservationDialog(false)}
        maxWidth="lg" // زيادة العرض هنا
        fullWidth
      >
        <DialogTitle>حجوزات الطاولة</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: "90vw", sm: 700, md: 900 } }}>
          <ShowTableReservation tableId={tableId} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowReservationDialog(false)}
            color="secondary"
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
      <form ref={formRef} onSubmit={handleSubmit}>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={customers.filter(
                (c) =>
                  // استبعاد العملاء باسم DeliveryCustomer و TakeAwayCustomer و TablesCustomer
                  c.name?.trim().toLowerCase() !== "deliverycustomer" &&
                  c.name?.trim().toLowerCase() !== "takeawaycustomer" &&
                  c.name?.trim().toLowerCase() !== "tablescustomer" &&
                  // استبعد العملاء الذين اسمهم فقط "customer" ولا يوجد لديهم رقم هاتف
                  !(
                    c.name &&
                    c.name.trim().toLowerCase() === "customer" &&
                    !(
                      (c.phone1 && c.phone1.trim() !== "") ||
                      (c.phone2 && c.phone2.trim() !== "") ||
                      (c.phone3 && c.phone3.trim() !== "")
                    )
                  )
              )}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.name || `عميل بدون اسم (${option.id})`
              }
              value={
                customers.find(
                  (c) => c.id.toString() === formData.customer_id
                ) ||
                (formData.customer_name
                  ? { name: formData.customer_name }
                  : null)
              }
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setFormData((prev) => ({
                    ...prev,
                    customer_id: "",
                    customer_name: newValue,
                    email: "",
                    customer_phones: [""],
                    customer_addresses: [""],
                  }));
                } else if (newValue && newValue.id) {
                  setFormData((prev) => ({
                    ...prev,
                    customer_id: newValue.id.toString(),
                    customer_name: newValue.name || "",
                    email: newValue.email || "",
                    customer_phones: [
                      newValue.phone1 || "",
                      ...(newValue.phone2 ? [newValue.phone2] : []),
                      ...(newValue.phone3 ? [newValue.phone3] : []),
                    ].filter(Boolean).length
                      ? [
                          newValue.phone1 || "",
                          ...(newValue.phone2 ? [newValue.phone2] : []),
                          ...(newValue.phone3 ? [newValue.phone3] : []),
                        ].filter(Boolean)
                      : [""],
                    customer_addresses: [
                      newValue.address1 || "",
                      newValue.address2 || "",
                      newValue.address3 || "",
                    ].filter(Boolean).length
                      ? [
                          newValue.address1 || "",
                          newValue.address2 || "",
                          newValue.address3 || "",
                        ].filter(Boolean)
                      : [""],
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    customer_id: "",
                    customer_name: "",
                    email: "",
                    customer_phones: [""],
                    customer_addresses: [""],
                  }));
                }
              }}
              onInputChange={(event, newInputValue) => {
                setFormData((prev) => ({
                  ...prev,
                  customer_name: newInputValue,
                  customer_id: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر عميل أو اكتب اسمه"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* هواتف العميل */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {formData.customer_phones.map((phone, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <TextField
                    label={`رقم الهاتف ${idx + 1}`}
                    value={phone}
                    onChange={(e) => handlePhoneChange(idx, e.target.value)}
                    sx={{ minWidth: { xs: 120, sm: 180 } }}
                    size="small"
                  />
                  {formData.customer_phones.length > 1 && (
                    <IconButton
                      aria-label="حذف"
                      color="error"
                      onClick={() => handleRemovePhone(idx)}
                      size="small"
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  )}
                  {idx === formData.customer_phones.length - 1 && (
                    <IconButton
                      aria-label="إضافة"
                      color="primary"
                      onClick={handleAddPhone}
                      size="small"
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* عناوين العميل */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {formData.customer_addresses.map((address, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <TextField
                    label={`عنوان ${idx + 1}`}
                    value={address}
                    onChange={(e) => handleAddressChange(idx, e.target.value)}
                    sx={{ minWidth: { xs: 120, sm: 180 } }}
                    size="small"
                  />
                  {formData.customer_addresses.length > 1 && (
                    <IconButton
                      aria-label="حذف"
                      color="error"
                      onClick={() => handleRemoveAddress(idx)}
                      size="small"
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  )}
                  {idx === formData.customer_addresses.length - 1 && (
                    <IconButton
                      aria-label="إضافة"
                      color="primary"
                      onClick={handleAddAddress}
                      size="small"
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>

          {Object.entries(formData).map(([key, value]) => {
            if (
              [
                "table_id",
                "root_id",
                "customer_id",
                "customer_name",
                "order_type",
                "customer_phones",
                "customer_addresses",
              ].includes(key)
            )
              return null;

            if (
              (formData.order_type === "tables" && key === "delivery_id") ||
              (formData.order_type === "delivery" &&
                key === "captain_order_id") ||
              (formData.order_type === "takeaway" &&
                (key === "captain_order_id" || key === "delivery_id"))
            )
              return null;

            if (key === "captain_order_id") {
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="captain-label">الكابتن</InputLabel>
                    <Select
                      labelId="captain-label"
                      id={key}
                      name={key}
                      value={value}
                      label="الكابتن"
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>اختر كابتن</em>
                      </MenuItem>
                      {captains.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            }

            if (key === "delivery_id") {
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="delivery-label">الدليفري</InputLabel>
                    <Select
                      labelId="delivery-label"
                      id={key}
                      name={key}
                      value={value}
                      label="الدليفري"
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>اختر دليفري</em>
                      </MenuItem>
                      {deliveries.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            }

            return (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  label={
                    key === "customer_name"
                      ? "اسم العميل"
                      : key === "email"
                      ? "البريد الإلكتروني"
                      : key.startsWith("customer_address")
                      ? `عنوان ${key.slice(-1)}`
                      : key === "order_type"
                      ? "نوع الطلب"
                      : key
                  }
                  name={key}
                  value={value}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
            );
          })}

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                title="إرسال (Ctrl+Enter)" // ✅ إضافة tooltip يوضح الاختصار
                sx={{
                  minWidth: { xs: 90, sm: 120 },
                  fontWeight: "bold",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  py: { xs: 1, sm: 1.5 },
                }}
                endIcon={
                  loading && <CircularProgress size={20} color="inherit" />
                }
              >
                {loading ? "جارٍ الإرسال..." : " إرسال"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {result && (
        <Box mt={3}>
          {result.success ? (
            <Alert severity="success">
              تم إنشاء الطلب بنجاح
              <pre
                style={{
                  direction: "ltr",
                  background: "#f6f6f6",
                  borderRadius: 6,
                  padding: 8,
                  marginTop: 8,
                  fontSize: "0.9em",
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </Alert>
          ) : (
            <Alert severity="error">خطأ: {result.message}</Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CreateOrderForm;
